import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { linkCurrentUserWithGoogle, linkCurrentUserWithApple, getLinkedProviderIdsLive } from '@/utils/firebase';
import {
  setHasSeenWebAccessOffer,
  setWebAccessLinkOfferNeverAsk,
} from '@/utils/storage';
import { queueUserPrefsCloudSync } from '@/utils/userPrefsCloudSync';
import { GoogleLogo } from '@/components/authLogos';
import { CONNECT_GOOGLE_FOR_WEB_PROMPT } from '@/utils/webAccessCopy';

const blogPrimaryButtonClass =
  'inline-flex w-full max-w-full mx-auto items-center justify-center gap-2 rounded-full px-6 py-3.5 font-body font-medium text-sm transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary disabled:opacity-60 disabled:hover:translate-y-0';

const blogOutlineButtonClass =
  'inline-flex w-full max-w-full mx-auto items-center justify-center gap-2 rounded-full px-6 py-3.5 font-body font-medium text-sm transition-all border border-theme-secondary bg-theme-light text-primary hover:bg-theme-hover disabled:opacity-60';

/**
 * After sign-in: prompt to link the other provider (same Firebase uid) for web + cross-device.
 * `missingProvider`: 'google' = user has Apple only; 'apple' = user has Google only.
 */
export const WebAccessLinkOffer = ({ open, onClose, missingProvider }) => {
  const [busy, setBusy] = useState(false);
  const [neverAgain, setNeverAgain] = useState(false);

  const finishLinked = () => {
    setHasSeenWebAccessOffer(true);
    queueUserPrefsCloudSync(100);
    setNeverAgain(false);
    onClose();
  };

  const handleNotNow = () => {
    if (neverAgain) {
      setWebAccessLinkOfferNeverAsk(true);
      setHasSeenWebAccessOffer(true);
      queueUserPrefsCloudSync(100);
    }
    setNeverAgain(false);
    onClose();
  };

  const handleLink = async () => {
    setBusy(true);
    try {
      if (missingProvider === 'google') {
        await linkCurrentUserWithGoogle();
        toast.success('Google account connected');
      } else {
        await linkCurrentUserWithApple();
        toast.success('Apple account connected');
      }
      await getLinkedProviderIdsLive();
      finishLinked();
    } catch (e) {
      toast.error(String(e?.message || 'Could not link account'));
    } finally {
      setBusy(false);
    }
  };

  const isGoogle = missingProvider === 'google';

  const dialogOpen = Boolean(open && missingProvider);

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(next) => {
        if (!next && !busy) handleNotNow();
      }}
    >
      <DialogContent
        className="w-full max-w-md mx-auto gap-3 border-theme-secondary bg-theme-light px-6 py-5 rounded-3xl shadow-ambient sm:max-w-md"
        onPointerDownOutside={(e) => {
          if (busy) e.preventDefault();
        }}
      >
        <DialogHeader className="space-y-2">
          {isGoogle ? (
            <DialogTitle className="sr-only">Connect Google for web access</DialogTitle>
          ) : (
            <DialogTitle className="font-heading text-lg text-primary leading-snug text-center">
              Use the same account on iPhone or iPad
            </DialogTitle>
          )}
          <DialogDescription asChild>
            <div className={isGoogle ? 'pt-0' : 'font-body text-secondary text-sm leading-relaxed pt-0 text-center space-y-2'}>
              {isGoogle ? (
                <p className="type-page-title leading-relaxed text-primary text-center whitespace-pre-wrap px-1">
                  {CONNECT_GOOGLE_FOR_WEB_PROMPT}
                </p>
              ) : (
                <>
                  <p>
                    Connect Sign in with Apple so Face ID and your other Apple devices can use this same account.
                  </p>
                  <p className="text-xs text-secondary/90">
                    You may see Apple&apos;s system sign-in sheet. It is the standard secure flow; this app does not
                    receive your Apple password.
                  </p>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 pt-1">
          <button
            type="button"
            className={blogPrimaryButtonClass}
            onClick={() => void handleLink()}
            disabled={busy}
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                Connecting…
              </>
            ) : isGoogle ? (
              <>
                <GoogleLogo className="h-5 w-5 shrink-0" />
                Connect to Google
              </>
            ) : (
              'Connect Apple'
            )}
          </button>
          <button type="button" className={blogOutlineButtonClass} onClick={handleNotNow} disabled={busy}>
            Not now
          </button>
          <label className="flex max-w-[14rem] cursor-pointer items-start gap-2 text-left font-body text-xs text-secondary">
            <input
              type="checkbox"
              checked={neverAgain}
              onChange={(e) => setNeverAgain(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>Don&apos;t show this again</span>
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
};
